import { displayName } from '@/fs/icons';
import { separator } from '@/shell/Menus/types';
import { useWindowStore } from '@/wm/store';
import { isRecycleBinNode } from '@/recycle/store';
import type { FsNode } from '@/fs/types';
import type { MenuItem } from '@/shell/Menus/types';
import type { AppArgs } from '@/wm/types';

/**
 * The pieces every context menu in the shell is built out of.
 *
 * A right-click menu is mostly quotation: the same six or seven rows appear on
 * the desktop, in a folder window and on the Recycle Bin, and they have to
 * agree about their wording, their mnemonics and what they do when the disk
 * turns out to be read-only. They are written once, here, and the per-target
 * builders next door choose which of them to show.
 */

/* --------------------------------------------------------------------------
   The default verb

   XP drew one row of a context menu in bold: the verb a double-click would
   run. `DropdownMenu` is shared with the menu bars, which have no such thing,
   so `ContextMenu` applies the emphasis itself — and asks here whether this
   particular menu has a default verb at all.

   It is always the first row, in this shell and in XP, which is what lets the
   emphasis be applied to the popup without counting rows past a separator.
   -------------------------------------------------------------------------- */

export const DEFAULT_VERB = '&Open';

/** The bold row: `Open` on a file, `Restore` on something in the bin. */
export function defaultVerbItem(label: string, onSelect: () => void): MenuItem {
  return { label, defaultVerb: true, onSelect };
}

/** True when `items` opens with the default verb, which is drawn in bold. */
export function hasDefaultVerb(items: MenuItem[]): boolean {
  return items[0]?.defaultVerb === true;
}

/* --------------------------------------------------------------------------
   Shared dialogs
   -------------------------------------------------------------------------- */

/**
 * Raise a message box on the desktop — no owner, so it centres on screen.
 * `height` overrides the manifest's 165px for the wordier ones, which would
 * otherwise have to scroll inside a box nobody can resize.
 */
export function msgbox(args: AppArgs, height?: number): void {
  useWindowStore
    .getState()
    .open('msgbox', args, height === undefined ? undefined : { rect: { h: height } });
}

/**
 * The answer to anything that would write to the disk. XP's own wording for
 * read-only media, with the reason underneath.
 */
export function readOnlyDisk(what: string): void {
  msgbox(
    {
      title: 'Windows',
      icon: 'error',
      text: `Unable to create the ${what}.\n\nThe disk is write-protected.`,
      detail: 'Remove the write-protection or use another disk.',
    },
    185,
  );
}

/** Copying anywhere lands here, since nowhere on this machine takes writes. */
function readOnlyCopy(what: string): void {
  msgbox(
    {
      title: 'Error Copying File or Folder',
      icon: 'error',
      text: `Cannot copy ${what}: The disk is write-protected.`,
      detail: 'Remove the write-protection or use another disk.',
    },
    185,
  );
}

/**
 * Search... appears on every folder in the shell and there is exactly one
 * answer to it, so it is written here rather than five times over. Explorer's
 * own Search button says the same thing.
 */
export function searchCompanion(): void {
  msgbox(
    {
      title: 'Search',
      icon: 'info',
      text: 'Search Companion is not installed in this copy of Windows.',
      detail:
        'No folder on this computer is more than three levels deep. Use the Folders bar instead.',
    },
    190,
  );
}

/**
 * The property sheet for a file or a folder.
 *
 * One sheet per object, which is what XP did: asking again for something that
 * is already on screen raises the sheet you already have rather than a second
 * copy of it. The sheets are not singletons, though — properties of two
 * different files are two different windows, and both stay open.
 */
export function showProperties(node: FsNode): void {
  // Except the bin's, which is a real sheet with real settings behind it.
  if (isRecycleBinNode(node)) {
    useWindowStore.getState().open('recycleprops', {});
    return;
  }

  const store = useWindowStore.getState();
  const open = store.windows.find((w) => w.appId === 'fileprops' && w.args.path === node.path);
  if (open) {
    store.focus(open.id);
    return;
  }

  store.open('fileprops', { path: node.path });
}

/* --------------------------------------------------------------------------
   Submenus that appear on more than one target
   -------------------------------------------------------------------------- */

/** What a command should call a run of files: the name, or a count. */
export function describe(nodes: readonly FsNode[]): string {
  return nodes.length === 1 ? `'${displayName(nodes[0])}'` : `these ${nodes.length} items`;
}

/**
 * Send To, in XP's own order — digits sort above letters, which is the only
 * reason the floppy drive came first on a machine that had not had one for
 * years.
 *
 * Every destination is a write except the mail client, and the mail client is
 * genuinely here, so that one really does open with the file named in the
 * subject line the way Windows filled it in.
 */
export function sendToMenu(nodes: readonly FsNode[]): MenuItem {
  const what = describe(nodes);
  const subject = nodes.length === 1 ? displayName(nodes[0]) : `${nodes.length} files`;

  return {
    label: 'Se&nd To',
    items: [
      {
        label: '3½ &Floppy (A:)',
        icon: '/assets/icons/drive.svg',
        onSelect: () =>
          msgbox(
            {
              title: 'Copy Item',
              icon: 'error',
              text: `Cannot copy ${what}: There is no disk in drive A:.`,
              detail: 'Insert a disk, and then try again.',
            },
            185,
          ),
      },
      {
        label: '&Compressed (zipped) Folder',
        icon: '/assets/icons/folder.svg',
        onSelect: () => readOnlyCopy(what),
      },
      {
        label: '&Desktop (create shortcut)',
        icon: '/assets/icons/lnk.svg',
        onSelect: () => readOnlyDisk('shortcut'),
      },
      {
        label: '&Mail Recipient',
        icon: '/assets/icons/outlook.svg',
        onSelect: () => {
          useWindowStore.getState().open('outlook', {
            compose: 1,
            subject: `Emailing: ${subject}`,
            body: `Your files are attached and ready to send with this message.\n\n${nodes
              .map((node) => displayName(node))
              .join('\n')}\n\n`,
          });
        },
      },
      {
        label: 'My D&ocuments',
        icon: '/assets/icons/my-documents.svg',
        onSelect: () => readOnlyCopy(what),
      },
    ],
  };
}

/**
 * New ▸, which every folder background and the desktop both carry. Nothing on
 * a write-protected disk can be created, so each row is a different sentence
 * of the same refusal — the point is that the menu is complete, not that the
 * commands work.
 */
export function newMenu(): MenuItem {
  return {
    label: 'Ne&w',
    items: [
      { label: '&Folder', icon: '/assets/icons/folder.svg', onSelect: () => readOnlyDisk("folder 'New Folder'") },
      { label: '&Shortcut', icon: '/assets/icons/lnk.svg', onSelect: () => readOnlyDisk('shortcut') },
      separator,
      {
        label: '&Text Document',
        icon: '/assets/icons/txt.svg',
        onSelect: () => readOnlyDisk("file 'New Text Document.txt'"),
      },
      {
        label: '&Bitmap Image',
        icon: '/assets/icons/image.svg',
        onSelect: () => readOnlyDisk("file 'New Bitmap Image.bmp'"),
      },
      {
        label: '&Briefcase',
        icon: '/assets/icons/folder.svg',
        onSelect: () => readOnlyDisk("briefcase 'New Briefcase'"),
      },
    ],
  };
}
